import { useEffect, useRef, useState } from "react";
import type { Movie } from "@/lib/movies";
import { formatDuration } from "@/lib/movies";

type PlayerState = "idle" | "loading" | "playing" | "paused" | "ended";

export default function PlayerDemo({ movie }: { movie: Movie }) {
  const [state, setState] = useState<PlayerState>("idle");
  const [progress, setProgress] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((timer) => window.clearInterval(timer));
    timers.current = [];
  };

  useEffect(() => clearTimers, []);

  const start = () => {
    clearTimers();
    setState("loading");
    setProgress(0);
    setLoadingProgress(0);

    const loader = window.setInterval(() => {
      setLoadingProgress((value) => {
        const next = value + Math.random() * 18 + 6;
        if (next >= 100) {
          window.clearInterval(loader);
          return 100;
        }
        return next;
      });
    }, 180);
    timers.current.push(loader);

    const starter = window.setTimeout(() => {
      setState("playing");
    }, 2400);
    timers.current.push(starter);
  };

  useEffect(() => {
    if (state !== "playing") return;
    const ticker = window.setInterval(() => {
      setProgress((value) => {
        if (value >= 100) {
          window.clearInterval(ticker);
          setState("ended");
          return 100;
        }
        return value + 0.7;
      });
    }, 200);
    return () => window.clearInterval(ticker);
  }, [state]);

  const elapsedSeconds = Math.round((progress / 100) * movie.duration * 60);
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  return (
    <div>
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
        <img
          src={movie.backdrop ?? movie.poster}
          alt={`${movie.title} 播放界面背景`}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ${
            state === "idle" ? "opacity-60 blur-[2px]" : "opacity-30 blur-sm"
          }`}
        />
        <div className="absolute inset-0 bg-background-50/70"></div>

        {state === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5">
            <button
              type="button"
              onClick={start}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
              aria-label="开始播放演示"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" aria-hidden="true"></i>
            </button>
            <p className="mt-5 font-heading text-base md:text-lg text-foreground-950 font-medium">
              点击开始观影演示
            </p>
            <p className="mt-2 text-xs md:text-sm text-foreground-600 max-w-sm leading-relaxed">
              播放区域为前端界面演示，用于展示观影流程，暂未接入真实片源。
            </p>
          </div>
        )}

        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
            <div className="w-12 h-12 rounded-full border-2 border-background-300 border-t-primary-500 animate-spin"></div>
            <p className="mt-5 text-sm text-foreground-800">正在准备演示片源…</p>
            <div className="mt-3 w-56 h-1 rounded-full bg-background-200 overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-200"
                style={{ width: `${Math.min(loadingProgress, 100)}%` }}
              ></div>
            </div>
            <p className="mt-2 text-[11px] text-foreground-500 font-label tracking-wider">
              {Math.min(Math.round(loadingProgress), 100)}%
            </p>
          </div>
        )}

        {(state === "playing" || state === "paused" || state === "ended") && (
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-background-50/20"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              {state === "ended" ? (
                <div className="text-center px-6">
                  <span className="w-14 h-14 rounded-full border border-background-300 flex items-center justify-center mx-auto text-foreground-700">
                    <i className="ri-check-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <p className="mt-4 font-heading text-base text-foreground-950">演示播放已结束</p>
                  <p className="mt-1.5 text-xs text-foreground-600">本段内容仅为界面演示，未使用任何真实影片素材。</p>
                  <button
                    type="button"
                    onClick={start}
                    className="mt-4 h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    重新播放演示
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 h-10">
                    {[0, 1, 2, 3, 4].map((bar) => (
                      <span
                        key={bar}
                        className={`w-1.5 rounded-full bg-primary-500/80 ${
                          state === "playing" ? "animate-pulse" : ""
                        }`}
                        style={{ height: `${12 + ((bar * 7) % 26)}px`, animationDelay: `${bar * 120}ms` }}
                      ></span>
                    ))}
                  </div>
                  <p className="mt-4 font-label text-[11px] tracking-[0.3em] text-foreground-600">
                    {state === "paused" ? "DEMO PAUSED" : "DEMO PLAYING"}
                  </p>
                </div>
              )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 bg-gradient-to-t from-background-50 to-transparent">
              <div className="h-1 rounded-full bg-background-300/80 overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-[width] duration-200"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-600">
                <span className="font-label tracking-wider">
                  {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")} /{" "}
                  {formatDuration(movie.duration)}
                </span>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline text-foreground-500">演示画面 · 非真实播放</span>
                  {state !== "ended" && (
                    <button
                      type="button"
                      onClick={() => setState(state === "playing" ? "paused" : "playing")}
                      className="w-7 h-7 rounded-full border border-background-300 flex items-center justify-center text-foreground-800 hover:border-primary-400 hover:text-primary-400 transition-colors cursor-pointer"
                      aria-label={state === "playing" ? "暂停演示" : "继续演示"}
                    >
                      <i
                        className={state === "playing" ? "ri-pause-line text-sm" : "ri-play-line text-sm"}
                        aria-hidden="true"
                      ></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <span className="absolute top-3 left-3 h-6 px-2 rounded bg-background-50/80 text-foreground-900 text-[11px] flex items-center gap-1 backdrop-blur-sm">
          <i className="ri-information-line" aria-hidden="true"></i>
          界面演示
        </span>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-md border border-secondary-200/50 bg-secondary-100/40 px-3.5 py-2.5">
        <i className="ri-error-warning-line text-secondary-900 mt-0.5" aria-hidden="true"></i>
        <p className="text-xs leading-relaxed text-secondary-900">
          提示：本站播放器仅用于界面与交互演示，不提供真实视频播放。若需接入正式片源，需要另行接入视频服务与版权授权。
        </p>
      </div>
    </div>
  );
}